/* eslint-disable no-restricted-imports */
import { useCallback, useEffect, useState } from 'react';

import { Box, Button, CircularProgress, TextField, Typography } from '@material-ui/core';
import { PersonAdd as PersonAddIcon } from '@material-ui/icons';

import { Autocomplete } from '@mui/material';
import { CreateClientDialog } from 'components/Contigo';
import PropTypes from 'prop-types';
import { clientService } from 'services';

import { useStyles } from './styles';

export default function CustomerSearchAsync({
  value,
  onChange,
  label = 'Buscar Cliente',
  placeholder = 'Digite o nome ou CNPJ do cliente...',
}) {
  const classes = useStyles();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  const searchClients = useCallback(async (searchText) => {
    if (!searchText || searchText.length < 2) {
      setOptions([]);
      return;
    }

    try {
      setLoading(true);
      const response = await clientService.searchClients({
        search: searchText,
        page: 1,
        limit: 20,
      });

      const clients = response.data?.clients || response.data || [];
      setOptions(clients);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchClients(inputValue);
    }, 800);

    return () => clearTimeout(timer);
  }, [inputValue, searchClients]);

  const handleClientCreated = (newClient) => {
    onChange(newClient);
    setOptions((prev) => [newClient, ...prev]);
  };

  return (
    <Box className={classes.container}>
      <Box display='flex' alignItems='center' justifyContent='space-between' mb={1}>
        {label && (
          <Typography variant='h6' gutterBottom>
            {label}
          </Typography>
        )}
        <Button
          variant='outlined'
          color='primary'
          size='small'
          startIcon={<PersonAddIcon />}
          onClick={() => setDialogOpen(true)}
          className={classes.addButton}
        >
          Novo Cliente
        </Button>
      </Box>

      <Autocomplete
        options={options}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          return `${option.NOME || ''} - ${option.APELIDO || option.CGC || ''}`;
        }}
        filterOptions={(x) => x}
        loading={loading}
        value={value}
        onChange={(_, newValue) => onChange(newValue)}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            variant='outlined'
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color='inherit' size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component='li' {...props}>
            <Box>
              <Typography variant='body1'>{option.NOME}</Typography>
              <Typography variant='body2' color='textSecondary'>
                {option.APELIDO && `${option.APELIDO} - `}
                {option.CGC}
              </Typography>
              {option.EMAIL && (
                <Typography variant='caption' color='textSecondary'>
                  {option.EMAIL}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        noOptionsText={
          inputValue.length < 2
            ? 'Digite pelo menos 2 caracteres para buscar'
            : 'Nenhum cliente encontrado'
        }
      />

      <CreateClientDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onClientCreated={handleClientCreated}
      />
    </Box>
  );
}

CustomerSearchAsync.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
};
